import { HTTPClient, AxiosRequestConfig, AxiosPromise, propsToDate, AxiosResponse } from "@abaco/web-common/src/HTTP";
import SsoVuexModule from "./state";
import { LoginResponse, UserLoginResponse } from "./models/LoginResponse";
import jwtDecode from "jwt-decode";
import { RefreshTokensResponse } from "./models/RefreshTokensResponse";
import { BaseUser } from "@abaco/web-common/src/BaseUser";
import { FunctionResponse, ContextFunctions } from "./models/FunctionResponse";
import { BASE } from "./models/Constants";
import { UserResponse, ContractInfo } from "./models/User";
import CompetencesResources from "./CompetencesResources";
import { ContextResponse } from "./models/Context";
import { CompetenceObj } from "./models/Competence";
import { GtagFunction } from "@abaco/web-common/src/app/Application";
import { UserProperties } from "./models/Properties";

export interface Messages {
    warnings: Message[];
    errors: Message[];
}

export interface Message {
    key: string;
    values: string[];
}

export type LoginCallback = (user: BaseUser) => void;
export type BasicLoginCallback = (http: HTTPClient, username: string, password: string) => Promise<LoginResponse>;

interface AuthToken {
    sub: string;
    iat: number;
    exp: number;
}

interface LoginMessages {
    warnings: Message[];
    errors: Message[];
}
interface MyStorage {
    getRefreshToken(): string | null;
    setRefreshToken(val: string | null): void;

    getAuthToken(): string | null;
    setAuthToken(val: string | null): void;

    getUser(): BaseUser | null;
    setUser(val: BaseUser | null): void;

    getContractValidity(): ContractInfo | undefined;
    setContractValidity(val: ContractInfo | undefined): void;

    getContractExpired(): boolean;
    setContractExpired(val: boolean): void;

    getUserFunctions(): ContextFunctions | null;
    setUserFunctions(val: ContextFunctions | null): void;

    getPrivacyChecks(): CompetenceObj | null;
    setPrivacyChecks(val: CompetenceObj | null): void;

    getUserProperties(): UserProperties | null;
    setUserProperties(val: UserProperties | null): void;

    clear(): void;
}

class SessionStore implements MyStorage {
    private setOrRemove(key: string, val: string | null) {
        if (val) {
            sessionStorage.setItem(key, val);
        } else {
            sessionStorage.removeItem(key);
        }
    }

    getRefreshToken(): string | null {
        return sessionStorage.getItem("rt");
    }

    setRefreshToken(val: string | null): void {
        this.setOrRemove("rt", val);
    }

    getAuthToken(): string | null {
        return sessionStorage.getItem("at");
    }

    setAuthToken(val: string | null): void {
        this.setOrRemove("at", val);
    }

    getUser(): BaseUser | null {
        const str = sessionStorage.getItem("user");
        return str ? JSON.parse(str) : null;
    }

    setUser(val: BaseUser | null): void {
        this.setOrRemove("user", val ? JSON.stringify(val) : null);
    }

    getContractValidity(): ContractInfo | undefined {
        const str = sessionStorage.getItem("cv");
        const obj = str ? JSON.parse(str) : undefined;
        if (obj) {
            const contractValidity: Record<string, Date> = {};
            Object.entries(obj).forEach((keyVal) => (contractValidity[keyVal[0]] = new Date(keyVal[1] as string)));
            return contractValidity as unknown as ContractInfo;
        }
        return;
    }

    setContractValidity(val: ContractInfo | undefined): void {
        this.setOrRemove("cv", val ? JSON.stringify(val) : null);
    }

    getContractExpired(): boolean {
        const str = sessionStorage.getItem("bce");
        return str === "true" ? true : false;
    }

    setContractExpired(val: boolean) {
        this.setOrRemove("bce", val ? "true" : "false");
    }

    getUserFunctions(): ContextFunctions {
        const str = sessionStorage.getItem("userFuncs");
        return str ? JSON.parse(str) : null;
    }

    setUserFunctions(val: ContextFunctions | null): void {
        this.setOrRemove("userFuncs", JSON.stringify(val));
    }

    getPrivacyChecks(): CompetenceObj | null {
        const str = sessionStorage.getItem("pvch");
        return str ? JSON.parse(str) : null;
    }

    setPrivacyChecks(val: CompetenceObj | null): void {
        this.setOrRemove("pvch", JSON.stringify(val));
    }

    getUserProperties(): UserProperties | null {
        const str = sessionStorage.getItem("userProps");
        return str ? new Map(Object.entries(JSON.parse(str))) : null;
    }

    setUserProperties(val: UserProperties | null): void {
        let parsedValue: string | null = null;
        if (val) {
            parsedValue = JSON.stringify(Array.from(val.entries()));
        }
        this.setOrRemove("userProps", parsedValue);
    }

    clear() {
        sessionStorage.removeItem("rt");
        sessionStorage.removeItem("at");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("cv");
        sessionStorage.removeItem("bce");
        sessionStorage.removeItem("userFuncs");
        sessionStorage.removeItem("pvch");
        sessionStorage.removeItem("userProps");
    }
}

const EXPIRE_WARNING = 1000 * 60 * 60 * 24 * 5;
const COMPETENCES = [
    { module: "ACCEPT_PRIVACY_1", software: "PORTAL" },
    { module: "ACCEPT_TOS", software: "PORTAL" },
    { module: "PRIVACY_2_MARK", software: "PORTAL" },
    { module: "PRIVACY_2_COM", software: "PORTAL" },
    { module: "PRIVACY_2_STATS", software: "PORTAL" },
];

export default class TokensResources {
    private configProviderAdded = false;
    private refreshClient = new HTTPClient();
    private inFlightRefreshRequest: AxiosPromise<RefreshTokensResponse> | null = null;
    protected authTokenExpiresAt = 0;
    private gtag!: GtagFunction;

    constructor(
        private http: HTTPClient,
        private state: SsoVuexModule,
        private compRes: CompetencesResources,
        private base_tenant_url: string,
        private onLogin?: LoginCallback,
        private session: MyStorage = new SessionStore(),
        private skipPrivacyCheck = false,
        private useSSO1 = false,
        private basicAuth?: BasicLoginCallback,
        private environment?: string,
        private useApiGw?: boolean
    ) {}

    public setGtag(gtag: GtagFunction) {
        this.gtag = gtag;
    }

    private async onLoginResponse(res: LoginResponse): Promise<Messages> {
        const messages = {
            warnings: res.warnings,
            errors: res.errors,
        };
        try {
            if (res.errors.length > 0 || !res.auth_token) {
                return messages;
            }

            this.parseToken(res.auth_token, res.user_id);
            this.session.setAuthToken(res.auth_token);
            this.session.setRefreshToken(res.refresh_token);

            this.addConfigProvider();

            await this.handleUserLoginResponse(messages, res);
        } catch (error) {
            console.error(error);
            throw error;
        }
        return messages;
    }

    private async handleUserLoginResponse(messages: LoginMessages, res: UserLoginResponse) {
        let isBasicUser = false;
        let contractInfo;
        let isContractExpired;

        const whoAmI = await this.isBasicUser();
        if (whoAmI) {
            isBasicUser = whoAmI?.is_basic || false;
            const contractSubscriptions = whoAmI?.subscriptions;
            contractInfo =
                contractSubscriptions && contractSubscriptions.length > 0 ? contractSubscriptions[0] : undefined;
            if (contractInfo) {
                contractInfo.from = new Date(contractInfo.from);
                contractInfo.to = new Date(contractInfo.to);
            }
            isContractExpired = this.checkContractValidity(messages, isBasicUser, contractInfo);
        }

        if (messages.errors.length > 0) {
            return messages;
        }

        this.onSuccessfulLogin(
            {
                dateFormat: res.dateFormat,
                description: res.description || res.user_id,
                locale: res.locale,
                username: res.user_id.toUpperCase(),
                real_username: res.username,
                isParentUser: res.isParentUser,
                tenant: res.tenant,
                isBasic: isBasicUser,
            },
            contractInfo,
            isContractExpired
        );

        if (!this.skipPrivacyCheck) {
            const com = [...COMPETENCES];
            if (this.environment === "r2u") {
                com.push({ module: "ACCEPT_TOS_18", software: "PORTAL" });
            }
            this.state.privacyChecks = await this.compRes.checkBulkCompetencesForUser(com);
            this.session.setPrivacyChecks(this.state.privacyChecks);
        }

        await Promise.all([this.loadUserFunctions(), this.loadUserProperties()]);
    }

    private async basicLoginResponse(res: LoginResponse, username: string, password: string): Promise<Messages> {
        const messages = {
            warnings: res.warnings,
            errors: res.errors,
        };

        if (res.errors.length > 0) {
            return messages;
        }

        res.auth_token = btoa(`${username}:${password}`);

        this.session.setAuthToken(res.auth_token);

        this.onSuccessfulLogin({
            dateFormat: res.dateFormat,
            description: res.description,
            locale: res.locale,
            username: res.user_id,
            real_username: res.username,
            isParentUser: res.isParentUser,
            tenant: res.tenant,
            isBasic: false,
        });
        return messages;
    }

    public async login(username: string, password: string): Promise<Messages> {
        if (this.basicAuth) {
            this.http.addRequestConfigProvider(async (config) => {
                config.auth = { username, password };
            });

            const loginResponse = await this.basicAuth(this.http, username, password);
            return this.basicLoginResponse(loginResponse, username, password);
        }
        const httpRes = await this.http.post<LoginResponse>(`${this.base_tenant_url}/login`, { username, password });
        return this.onLoginResponse(httpRes.data);
    }

    public async proxyLogin(username: string, password: string, proxyService: string) {
        const httpRes = await this.http.post<LoginResponse>(`${BASE}/proxy-login/${proxyService}`, {
            username,
            password,
        });
        return this.onLoginResponse(httpRes.data);
    }

    public async exchangeSso1Token(ssoToken: string, loginContext?: string) {
        const httpRes = await this.http.post<LoginResponse>(`${BASE}/exchange-sso1`, { ssoToken, loginContext });
        return this.onLoginResponse(httpRes.data);
    }

    private onSuccessfulLogin(user: BaseUser, contractInfo?: ContractInfo | undefined, isContractExpired = false) {
        this.session.setContractExpired(isContractExpired);
        this.state.basicContractExpired = isContractExpired;
        this.session.setContractValidity(contractInfo);
        this.state.basicContractValidity = contractInfo;
        this.session.setUser(user);
        this.state.user = user;
        if (this.onLogin) {
            this.onLogin(this.state.user);
        }
    }

    private addConfigProvider() {
        if (!this.configProviderAdded) {
            this.http.addRequestConfigProvider((config) => this.provideConfig(config));
            this.configProviderAdded = true;
        }
    }

    public async whoAmI() {
        const response = await this.http.get<UserLoginResponse>(`${this.base_tenant_url}/who-am-i`);
        const userLoginResponse = response.data;
        const messages = await this.handleUserLoginResponse(
            { errors: userLoginResponse.errors || [], warnings: userLoginResponse.warnings || [] },
            userLoginResponse
        );

        return messages;
    }

    public async resumeSession(): Promise<boolean> {
        let user = this.session.getUser();
        if (!user && this.useApiGw) {
            await this.whoAmI();
            user = this.session.getUser();
        }
        if (!user) {
            return false;
        }

        const contractValidity = this.session.getContractValidity();
        const isContractExpired = this.session.getContractExpired();
        this.onSuccessfulLogin(user, contractValidity, isContractExpired);

        if (!this.useApiGw) {
            const token = this.session.getAuthToken();
            if (!token) {
                return false;
            }

            if (this.basicAuth) {
                this.http.addRequestConfigProvider(async (config) => {
                    config.headers = { ...config.headers, Authorization: `Basic ${token}` };
                });
                return true;
            }

            this.parseToken(token, user.username);
            this.addConfigProvider();
        }

        this.state.userFunctions = this.session.getUserFunctions();
        this.state.privacyChecks = this.session.getPrivacyChecks();
        this.state.userProperties = this.session.getUserProperties();
        return true;
    }

    public logout() {
        this.session.clear();
        this.authTokenExpiresAt = 0;
        this.state.user = null;
    }

    private async isBasicUser(): Promise<UserResponse | undefined> {
        const url = "/sitiagri-rest-api/api_basic/v1/user_registry/who_am_i";
        try {
            const res = await this.http.get(url);
            if (res.data && res.data && res.data.basic && res.data.basic.from && res.data.basic.to) {
                propsToDate(res.data, ["basic.from", "basic.to"]);
            }
            return res.data;
        } catch (error) {
            console.error(error);
            return;
        }
    }

    public async loadUserFunctions() {
        const res = await this.http.get<FunctionResponse[]>(`${this.base_tenant_url}/user-functions`);
        const functions = res.data || [];
        this.state.userFunctions = functions.reduce((accum, f) => {
            const cur = accum[f.context_id];
            if (cur) {
                cur.push(f.function_name);
            } else {
                accum[f.context_id] = [f.function_name];
            }
            return accum;
        }, {} as ContextFunctions);
        this.session.setUserFunctions(this.state.userFunctions);
    }

    public async loadUserProperties() {
        const res = await this.http.get<UserProperties>(`${this.base_tenant_url}/user-properties`);
        const properties = new Map(Object.entries(res.data));

        this.state.userProperties = properties;
        this.session.setUserProperties(properties);

        if (properties.has("CRM")) {
            this.gtag("event", "dimension1", { CRM: properties.get("CRM") });
        }
    }

    public async loadUserContexts(): Promise<ContextResponse> {
        const res = await this.http.get<string[]>(`${this.base_tenant_url}/user-contexts`);
        const contexts = new Map<string, boolean>();
        res.data?.forEach((ctx) => {
            contexts.set(ctx, true);
        });
        this.state.userContexts = contexts;
        return contexts;
    }

    public async getProxyloginServices(proxyServicesId?: string[]): Promise<string[]> {
        const params = {
            filterBy: proxyServicesId && proxyServicesId.length ? proxyServicesId : undefined,
        };
        const res = await this.http.get(`${BASE}/proxy-login/services`, { params });
        return res.data || [];
    }

    // For testing
    protected parseToken(authToken: string, expectedUser?: string): string {
        const decoded = jwtDecode(authToken) as AuthToken;
        if (!decoded.sub || (expectedUser && expectedUser.toUpperCase() !== decoded.sub.toUpperCase())) {
            this.session.clear();
            this.authTokenExpiresAt = 0;
            throw new Error("Bad subject");
        }

        // Adjust the expiration time based on the possbile time difference between sever and client
        const serverNow = new Date(decoded.iat * 1000);
        const now = new Date();
        const deltaMs = serverNow.getTime() - now.getTime();

        this.authTokenExpiresAt = (decoded.exp - 20) * 1000 - Math.abs(deltaMs); // Remove some seconds to have margin on expire time
        return decoded.sub;
    }

    private async provideConfig(config: AxiosRequestConfig) {
        const authToken = this.session.getAuthToken();
        if (!authToken) {
            return;
        }

        const refreshed = await this.refreshTokens();
        if (!refreshed) {
            return;
        }

        if (this.useSSO1) {
            config.params = config.params || {};
            if (!config.params.SSO_TOKEN) {
                config.params.SSO_TOKEN = await this.getSSOToken();
            }
        }

        config.headers = config.headers || {};
        config.headers["Authorization"] = `JWT ${this.session.getAuthToken()}`;
    }

    public async getSSOToken(): Promise<string> {
        if (this.useApiGw) {
            return "";
        }
        let authToken = this.session.getAuthToken();
        if (!authToken) {
            throw new Error("Not authenticated");
        }

        const refreshed = await this.refreshTokens();
        authToken = refreshed ? this.session.getAuthToken() : null;
        if (!authToken) {
            throw new Error("No auth token found");
        }

        return `j2_${authToken}`;
    }

    public getAuthTokenExpiresAt(): number {
        return this.authTokenExpiresAt;
    }

    // For testing
    protected callRefreshToken(): AxiosPromise<RefreshTokensResponse> {
        return this.refreshClient.post(`${this.base_tenant_url}/refresh`, {
            refresh_token: this.session.getRefreshToken(),
        });
    }

    // For testing
    protected async refreshTokens(): Promise<boolean> {
        if (!this.session.getRefreshToken()) {
            return false;
        }

        const now = new Date();
        if (this.authTokenExpiresAt > now.getTime()) {
            return true;
        }

        let ret = true,
            createdByMe = false;
        try {
            if (!this.inFlightRefreshRequest) {
                this.inFlightRefreshRequest = this.callRefreshToken();
                createdByMe = true;
            }

            /* TOM: I think that a local copy is not really needed
             *      but am not 100% sure that the assignment: this.inFlightRefreshRequest = null;
             *      that I do later will not create issues on the ES5 polyfilled and transpiled javascript
             *      so I copy the variable, just in case...
             */
            const localCopy = this.inFlightRefreshRequest;
            const ares = await localCopy;
            const res = ares.data;
            this.parseToken(res.auth_token, this.state.user ? this.state.user.username : undefined);
            this.session.setAuthToken(res.auth_token);
            this.session.setRefreshToken(res.refresh_token);

            if (!this.state.user) {
                // If we are here, it means that the session is still valid but we lost hte javascript state
                // I.e. a user pressed refresh on the web browser
                const user = this.session.getUser();
                if (user) {
                    this.onSuccessfulLogin(user);
                }
            }
        } catch (err) {
            this.session.clear();
            this.authTokenExpiresAt = 0;
            this.state.user = null;

            ret = false;
        }

        if (createdByMe) {
            this.inFlightRefreshRequest = null;
        }

        return ret;
    }

    private checkContractValidity(messages: Messages, isBasicUser: boolean, contractInfo?: ContractInfo | undefined) {
        if (!contractInfo) {
            return false;
        }
        if (isBasicUser && !contractInfo) {
            messages.errors.push({ key: "cannot-verify-basic-user-contract", values: [] });
            return;
        }
        if (contractInfo) {
            const now = new Date();
            const stillNotValid = contractInfo.from.getTime() - now.getTime();
            const expired = now.getTime() - contractInfo.to.getTime();
            if (expired > 0) {
                // TODO change to return true when redirect working
                // messages.errors.push({
                //     key: 'basic-contract-expired',
                //     values: this.computeDays(expired).split("-")
                // })
                return true;
            }
            if (stillNotValid > 0) {
                messages.errors.push({
                    key: "basic-contract-still-not-valid",
                    values: this.computeDays(stillNotValid).split("-"),
                });
                return;
            }
            if (expired < 0 && -1 * expired < EXPIRE_WARNING) {
                messages.warnings.push({
                    key: "contract-will-expire-in",
                    values: this.computeDays(-1 * expired).split("-"),
                });
            }
        }
    }

    private computeDays(ms: number) {
        const HOUR = 60 * 60 * 1000;
        const DAY = 24 * HOUR;
        const days = Math.ceil(ms / DAY);
        const daysms = ms % DAY;
        const hours = Math.ceil(daysms / HOUR);

        return days < 1 ? `${hours}-${"hours"}` : `${days}-${"days"}`;
    }
}
